import React from "react";

import { GradientDefs } from "../../GradientDefs";

/**
 * @component CircleSlashIcon
 * @description
 *
 * The React Icon component is a visual element that displays an icon to represent a concept, object, or action.
 * The Icon component is
 * customizable, allowing for variations in size, color, and style to fit the needs of the application.
 *
 * A ring (based on CircleIcon) crossed by a single diagonal line at 45 degrees,
 * running from the bottom-left to the top-right. The slash matches the ring's
 * stroke weight (~2.222 units).
 *
 * @prop {string} [gradient] - Optional CSS linear-gradient string to apply a custom fill.
 * Format: "linear-gradient(<angle>deg, <color1>, <color2>, ...)"
 *
 * @example
 * return (
 * <CircleSlashIcon gradient="linear-gradient(135deg, #4da2ed, #f72222)" />
 * )
 *
 * @example
 * return (
 *   <CircleSlashIcon className="mr-2" />
 * )
 *
 * @see
 * https://system.activecollab.com/?path=/story/foundation-icons-icons--icons
 * @see
 * https://design.activecollab.com/docs/foundations/icons
 */
const CircleSlashIcon = React.forwardRef(
  (
    {
      gradient,
      ...props
    }: React.SVGProps<SVGSVGElement> & {
      gradient?: string;
    },
    ref?: React.Ref<SVGSVGElement>
  ) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      data-testid="CircleSlashIcon"
      fill={
        gradient
          ? "url(#icon-gradient)"
          : props?.fill
          ? props.fill
          : "var(--color-theme-600)"
      }
      focusable={false}
      ref={ref}
      {...props}
    >
      <GradientDefs gradient={gradient} />
      <path d="M12 4.222a7.778 7.778 0 1 0 0 15.556 7.778 7.778 0 0 0 0-15.556M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20" />
      <path d="M5.716 19.856 19.856 5.716l-1.571-1.571L4.144 18.284z" />
    </svg>
  )
);
CircleSlashIcon.displayName = "CircleSlashIcon";
export default CircleSlashIcon;
